import React, { useState, useEffect, useRef } from "react";
import FormatMoney from "../../helpers";
import { T } from "./theme";
import {
  StyledLabel,
  StyledErrorText,
  InputShell,
  SelectShell,
  Affix,
  StyledInput,
  StyledSelect,
  SelectChevron,
  StyledBtn,
  FullPageCenter,
  DangerBanner
} from "./FormControls.styles";

export function Label({ htmlFor, children }) {
  return <StyledLabel htmlFor={htmlFor}>{children}</StyledLabel>;
}

export function ErrorText({ children }) {
  return <StyledErrorText>{children}</StyledErrorText>;
}

export function TextInput({
  id,
  name,
  value,
  onChange,
  onBlur,
  prefix,
  suffix,
  invalid,
  placeholder,
  autoFocus,
  type = "text",
  numeric = false
}) {
  const [focused, setFocused] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (autoFocus) ref.current?.focus();
  }, [autoFocus]);

  return (
    <InputShell invalid={invalid} focused={focused}>
      {prefix && <Affix position="left">{prefix}</Affix>}
      <StyledInput
        id={id}
        name={name}
        ref={ref}
        value={value}
        placeholder={placeholder}
        type={type}
        inputMode={numeric ? "numeric" : undefined}
        numeric={numeric}
        autoComplete="off"
        onFocus={(e) => {
          setFocused(true);
          e.target.select();
        }}
        onBlur={(e) => {
          setFocused(false);
          onBlur?.(e);
        }}
        onChange={onChange}
      />
      {suffix && (
        <Affix position="right" size="small">
          {suffix}
        </Affix>
      )}
    </InputShell>
  );
}

export function SelectInput({ id, name, value, onChange, onBlur, invalid, children }) {
  const [focused, setFocused] = useState(false);
  return (
    <SelectShell invalid={invalid} focused={focused}>
      <StyledSelect
        id={id}
        name={name}
        value={value}
        hasValue={!!value}
        onChange={onChange}
        onFocus={() => setFocused(true)}
        onBlur={(e) => {
          setFocused(false);
          onBlur?.(e);
        }}>
        {children}
      </StyledSelect>
      <SelectChevron
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        stroke={T.inkSoft}
        strokeWidth="2">
        <path d="M6 9l6 6 6-6" />
      </SelectChevron>
    </SelectShell>
  );
}

export function Btn({ children, type = "button", onClick, variant = "primary", disabled, full }) {
  return (
    <StyledBtn
      type={type}
      onClick={onClick}
      disabled={disabled}
      variant={variant}
      full={full}
      className="rm-btn">
      {children}
    </StyledBtn>
  );
}

export function Spinner() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" className="rm-spin">
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeOpacity=".3" strokeWidth="2" />
      <path d="M21 12a9 9 0 0 0-9-9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

export function Loading() {
  return (
    <FullPageCenter tint>
      <style>{`
        .rm-spin { animation: rmspin 1s linear infinite; }
        @keyframes rmspin { to { transform: rotate(360deg) } }
        @media (prefers-reduced-motion: reduce) { * { animation: none !important } }
      `}</style>
      <Spinner />
    </FullPageCenter>
  );
}

export function NoLoanOfficer() {
  return (
    <FullPageCenter>
      <DangerBanner>No access for this loan officer</DangerBanner>
    </FullPageCenter>
  );
}

export function CountUp({ target }) {
  const [n, setN] = useState(target);
  const from = useRef(target);
  useEffect(() => {
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) {
      setN(target);
      from.current = target;
      return;
    }
    const start = performance.now();
    const a = from.current;
    let raf;
    const tick = (t) => {
      const p = Math.min((t - start) / 650, 1);
      setN(a + (target - a) * (1 - Math.pow(1 - p, 3)));
      if (p < 1) raf = requestAnimationFrame(tick);
      else from.current = target;
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target]);
  return <>{`$${FormatMoney(Math.round(n))}`}</>;
}
